import type React from "react";
import { useMemo, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import memberStyles from "~/components/Project/projectMemberContent/index.module.scss";
import Button from "~/components/ui/button";
import { sampleProject } from "~/constants/sampleProject";
import { sampleKnowledgeItems } from "~/data/sampleProject";
import detailStyles from "~/features/projectTop/projectTopListContent/index.module.scss";
import { useTutorialAccessGuard } from "~/hooks/useTutorialAccessGuard";
import projectTopStyles from "../../projectTop/index.module.scss";
import TutorialDashboard from "../dashboard";
import { TutorialKnowledgeContent } from "../knowledge";
import styles from "./index.module.scss";

const members = [
  { name: "佐藤 太郎", role: "オーナー" },
  { name: "鈴木 花子", role: "編集者" },
  { name: "田中 一郎", role: "閲覧者" },
];

const formatDate = (value?: string) => {
  if (!value) return "指定なし";
  return new Date(value).toISOString().split("T")[0];
};

const tabTitles = ["ダッシュボード", "詳細情報", "メンバー", "ナレッジ"] as const;

const TutorialProjectTop: React.FC = () => {
  useTutorialAccessGuard();
  const [activeTab, setActiveTab] = useState(0);
  const shouldHighlightTabs = false;

  const projectSummary = useMemo(
    () => [
      { label: "プロジェクト名", value: sampleProject.name },
      { label: "説明", value: sampleProject.description ?? "―" },
      {
        label: "契約期間",
        value: `${formatDate(sampleProject.start_date)} ～ ${formatDate(sampleProject.end_date)}`,
      },
      { label: "クライアント名", value: sampleProject.client?.name ?? "―" },
      { label: "クライアントURL", value: sampleProject.client?.url ?? "―" },
    ],
    [],
  );

  const pendingKnowledgeCount = useMemo(
    () => sampleKnowledgeItems.filter((item) => item.status === "pending").length,
    [],
  );

  const handleTabSelect = (index: number) => {
    setActiveTab(index);
  };

  const baseTabPanelClass = projectTopStyles["custom-tab-panel"] ?? "";

  const buildTabPanelClass = () =>
    [baseTabPanelClass, styles["tabPanel"]].filter(Boolean).join(" ");

  return (
    <div className={styles["wrapper"]} data-tour-id="tutorial-project-top">
      <h1 className="text-2xl font-medium">{sampleProject.name}</h1>
      <div className={styles["period"]}>
        期間: {formatDate(sampleProject.start_date)} ～ {formatDate(sampleProject.end_date)}
      </div>

      <Tabs
        className={projectTopStyles["tab"]}
        selectedIndex={activeTab}
        onSelect={handleTabSelect}
      >
        <TabList className={projectTopStyles["tab-list"]}>
          {tabTitles.map((title, index) => (
            <Tab
              key={title}
              className={[
                projectTopStyles["tab-list-name"],
                shouldHighlightTabs ? styles["tabHighlight"] : "",
              ]
                .filter(Boolean)
                .join(" ")}
              selectedClassName={projectTopStyles["tab-list-name--selected"]}
            >
              <span>{title}</span>
              {index === 3 && pendingKnowledgeCount > 0 && (
                <span className={projectTopStyles["knowledge-badge"]}>{pendingKnowledgeCount}</span>
              )}
            </Tab>
          ))}
        </TabList>

        <TabPanel className={buildTabPanelClass()}>
          <div className={styles["dashboardPanel"]}>
            <TutorialDashboard />
          </div>
        </TabPanel>

        <TabPanel className={buildTabPanelClass()}>
          <div className={detailStyles["ProjectTopListContent"]}>
            <div className={detailStyles["title"]}>
              <h3>詳細情報</h3>
              <Button label="編集" styleType="blue" disabled />
            </div>
            <ul className={detailStyles["ul"]}>
              {projectSummary.map((item) => (
                <li key={item.label} className={detailStyles["li"]}>
                  <div>{item.label}</div>
                  <div>{item.value}</div>
                </li>
              ))}
            </ul>
          </div>
        </TabPanel>

        <TabPanel className={buildTabPanelClass()}>
          <div className={memberStyles["ProjectTopListContent"]}>
            <div className={memberStyles["title"]}>
              <h3>メンバー</h3>
              <Button label="メンバー追加" styleType="blue" disabled />
            </div>
            <ul className={memberStyles["memberList"]}>
              {members.map((member) => (
                <li key={member.name} className={memberStyles["memberItem"]}>
                  <div className={memberStyles["memberDetails"]}>
                    <span>{member.name}</span>
                    <span
                      className={
                        member.role === "オーナー"
                          ? memberStyles["adminRole"]
                          : member.role === "編集者"
                            ? memberStyles["memberRole"]
                            : memberStyles["userRole"]
                      }
                    >
                      {member.role}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </TabPanel>

        <TabPanel className={buildTabPanelClass()}>
          <TutorialKnowledgeContent tourId={null} />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default TutorialProjectTop;
